import { Component, ErrorInfo, ReactNode } from 'react';
import './error-boundary.css';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * React Error Boundary.
 * Catches unhandled errors in the component tree and renders a fallback UI.
 * Uses CSS classes instead of inline styles.
 *
 * Note: Text is hardcoded English because class components cannot use hooks (useI18n).
 * This is intentional — the ErrorBoundary wraps the entire app including providers,
 * so i18n may not be available when an error occurs.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('ErrorBoundary caught error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="cb-error-boundary">
          <div className="cb-error-boundary__code">500</div>
          <h1 className="cb-error-boundary__title">A disturbance in the Force</h1>
          <p className="cb-error-boundary__message">
            {this.state.error?.message ?? 'An unexpected error occurred.'}
          </p>
          <button
            className="cb-error-boundary__reload"
            onClick={() => globalThis.location.reload()}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
